import ButtonVariants from "@core-components/button/ButtonVariants";
import SSTooltip from "@core-components/tooltip/Tooltip";
import useAppTranslation from "@hooks/language/useAppTranslation";
import {
  Divider,
  Grid,
  Group,
  Image,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { useLoLRankedAsBasketAccount } from "@modules/order/hooks/useBasketItem.hook";
import type { ILoLAccount } from "@modules/ranked/models/IRankedSmurf.models";
import useBuyModalStore from "@store/buyModal.store";
import useRankedDetailsModalStore from "@store/rankedDetails.store";
import {
  IconArrowRight,
  IconCircleCheckFilled,
  IconInfoSquareRoundedFilled,
} from "@tabler/icons-react";

interface IRankDetailsProps {
  account: ILoLAccount;
}

export const RankDetails: React.FC<IRankDetailsProps> = ({ account }) => {
  const { t } = useAppTranslation("pages.ranked.rankedDetails");
  const { t: tRank } = useAppTranslation("products.lol");
  const { open } = useBuyModalStore();
  const { close } = useRankedDetailsModalStore();
  const basketItem = useLoLRankedAsBasketAccount(account);

  const handleBuyModal = () => {
    open(basketItem, () => close());
  };

  const detailLine = (label: string, value: string, icon?: string) => (
    <Grid.Col span={4}>
      <Text className="font-bold text-[12px] text-primary-white md:text-[16px]">
        {label}
      </Text>
      <Text className="font-inter text-[15px] text-primary-dark-text">
        {icon && (
          <Image className="inline-block pr-1 pb-1" h={24} src={icon} w={24} />
        )}{" "}
        {value}
      </Text>
    </Grid.Col>
  );

  const gridDivider = () => (
    <Grid.Col span={12}>
      <Divider className="border-primary-lighter" />
    </Grid.Col>
  );

  const getRank = (rank: string, subRank: number | undefined) => {
    return tRank(`${rank}.rank`, {
      subRank: subRank,
    });
  };

  return (
    <Stack className="xl:basis-1/2">
      <Grid>
        {detailLine(
          t("currentRank"),
          getRank(
            account.rankedInformation.currentRankInformation.rank,
            account.rankedInformation.currentRankInformation.subRank,
          ),
          account.rankedInformation.currentRankInformation.icon,
        )}
        {detailLine(
          t("flexRank"),
          getRank(
            account.rankedInformation.currentFlexRankInformation.rank,
            account.rankedInformation.currentFlexRankInformation.subRank,
          ),
          account.rankedInformation.currentFlexRankInformation.icon,
        )}
        {detailLine(
          t("previousRank"),
          getRank(
            account.rankedInformation.previousRankInformation.rank,
            account.rankedInformation.previousRankInformation.subRank,
          ),
          account.rankedInformation.previousRankInformation.icon,
        )}
        {gridDivider()}
        {detailLine(t("server"), account.region)}
        {detailLine(t("level"), account.rankedInformation.level.toString())}
        {detailLine(
          t("winRate"),
          `${Math.floor(account.rankedInformation.winRate)}%`,
        )}
        {gridDivider()}
        {detailLine(
          t("rankedPlayed"),
          account.rankedInformation.rankedPlayed.toString(),
        )}
        {detailLine(t("blueEssence"), account.blueEssence.toString())}
        {detailLine(
          t("riotPoints"),
          account.rankedInformation.riotPoints.toString(),
        )}
        {gridDivider()}
        {detailLine(t("champions"), account.champions.length.toString())}
        {detailLine(
          t("permanentSkins"),
          account.skins.filter((s) => !s.pivot_is_shard).length.toString(),
        )}
        {detailLine(
          t("skinShards"),
          account.skins.filter((s) => s.pivot_is_shard).length.toString(),
        )}
      </Grid>

      <Stack>
        <Group wrap="nowrap" gap={8}>
          <ThemeIcon variant="transparent">
            <IconCircleCheckFilled />
          </ThemeIcon>
          <Text className="font-inter text-[14px] text-primary-white">
            {t("features.modifiable")}
          </Text>
        </Group>

        <Group wrap="nowrap" gap={8}>
          <ThemeIcon variant="transparent">
            <IconCircleCheckFilled />
          </ThemeIcon>
          <Text className="font-inter text-[14px] text-primary-white">
            {t("features.instantDelivery")}
          </Text>
        </Group>

        <Group wrap="nowrap" gap={8}>
          <ThemeIcon variant="transparent">
            <IconCircleCheckFilled />
          </ThemeIcon>
          <Text className="font-inter text-[14px] text-primary-white">
            <SSTooltip
              label={t("features.warranty.label")}
              text={
                t("features.warranty.tooltip", {
                  returnObjects: true,
                }) as string[]
              }
            />
          </Text>
        </Group>
        <Group align="end">
          <Text className="font-bold font-inter text-[36px] text-primary-white leading-9">
            {account.product.price}€
          </Text>
          <Text className="font-bold font-inter text-[14px] text-primary-dark-text">
            {t("currency")}
          </Text>
        </Group>
        <ButtonVariants.Gradient
          onClick={handleBuyModal}
          label={
            <>
              {t("buyButton")}{" "}
              <ThemeIcon className="ml-2" color="white" variant="transparent">
                <IconArrowRight />
              </ThemeIcon>
            </>
          }
          size="lg"
        />
        <Group gap={2} justify="center">
          <ThemeIcon
            classNames={{
              root: "text-secret-blue-light",
            }}
            variant="transparent"
          >
            <IconInfoSquareRoundedFilled />
          </ThemeIcon>
          <Text className="align-middle font-inter text-[14px] text-secret-blue-light">
            {t("uniqueAccount")}
          </Text>
        </Group>
      </Stack>
    </Stack>
  );
};
